import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { logAndAddError } from '@dotfile/frontend/shared/common';

type YousignSdkContextType = {
  loaded: boolean;
};

const YousignSdkContext = createContext<YousignSdkContextType>({
  loaded: false,
});

export const useYousignSdk = () => {
  return useContext(YousignSdkContext);
};

declare global {
  interface Window {
    Yousign: {
      new (config: {
        signatureLink: string;
        iframeContainerId: string;
        isSandbox: boolean;
        iframeAttributes?: {
          referrerPolicy?: string;
        };
      }): {
        onSuccess: (callback: () => void) => void;
        destroy?: () => void;
      };
    };
    onYousignReady?: () => void;
  }
}

export const YousignSdkProvider = ({ children }: PropsWithChildren) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (window.Yousign) {
      setLoaded(true);
      return;
    }

    window.onYousignReady = () => {
      setLoaded(true);
    };

    let node: HTMLScriptElement;
    try {
      const scriptElement = document.createElement('script');
      scriptElement.type = 'application/javascript';
      scriptElement.id = 'yousign-sdk-js';
      scriptElement.src = 'https://cdn.yousign.tech/iframe-sdk-1.6.0.js';
      scriptElement.integrity =
        'sha384-R+s/yjLK/cbrNa3CN+AZ62oJ0C2aGzcSsGYpTnX/SI89pmrEGlAWUCdzkR2RuVR6';
      scriptElement.crossOrigin = 'anonymous';
      scriptElement.onload = () => {
        if (window.Yousign) {
          setLoaded(true);
        }
      };
      scriptElement.onerror = () => {
        logAndAddError('Error loading Yousign SDK script');
      };

      node = document.head.appendChild(scriptElement);
    } catch (error) {
      logAndAddError('Error appending Yousign SDK script', { error });
    }

    // Remove script and cleanup
    return () => {
      if ('onYousignReady' in window) delete window.onYousignReady;
      node?.remove();
    };
  }, []);

  const value = useMemo(() => ({ loaded }), [loaded]);

  return (
    <YousignSdkContext.Provider value={value}>
      {children}
    </YousignSdkContext.Provider>
  );
};
